import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Button from 'Components/DesignSystem/Button/Button';
import { Hashtag, Return } from 'Components/DesignSystem/Iconography/Icons';
import Tag from 'Components/DesignSystem/Tag/Tag';
import CustomAttributeTag from 'Components/Common/CustomAttributeTag';

// Types
import { ExpandedRowContentProps, ProductMetadata, ProductAttribute, ValueStatus } from '../../types';
import { TagProps } from 'types/DesignSystem/Tag';

// Utilities
import { getValues } from '../../utils';

// Modals
import AttributeValuesModal from '../../modals/AttributeValuesModal';
// Reuse the existing select-with-create for the pending-new attribute name picker.
import SingleSelectWithCreate from '../../modals/components/SingleSelectWithCreate';

// Max visible values before showing "+ N" and "Show All"
const MAX_VISIBLE_VALUES = 3;

// Scroll constants — show at most 5 attribute rows before scrolling
const MAX_VISIBLE_ROWS = 5;
const ROW_HEIGHT_PX = 44;

// Default column widths matching the SKU matched parent table colgroup
const DEFAULT_COL_WIDTHS = ['4%', '3%', '19%', '11%', '9%', '15%', '10%', '6%', '6%', '6%', '5%', '4%'];

// Single-section column widths: [empty spacer] | [#] | [Attribute] | [Attribute Value]
const SINGLE_SECTION_COL_WIDTHS = ['5%', '5%', '30%', '60%'];

type AddValueOption = { value: string; label: string };

/**
 * `+` icon button at the end of an attribute's inline value list (edit mode,
 * no overflow). Opens a fixed-position popover with Create link + search +
 * checkbox list of available values. Each checkbox toggle is live: clicking
 * an unchecked option calls `onAdd`, clicking a checked option calls
 * `onRemove` (per ticket 11443 — apply-per-check, not apply-on-close).
 */
const InlineAddValueButton: React.FC<{
  selectedValues: string[];
  loadOptions: (search: string, page: number) => Promise<{ options: AddValueOption[]; hasMore: boolean }>;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  onCreateNew: () => void;
  disabled?: boolean;
}> = ({ selectedValues, loadOptions, onAdd, onRemove, onCreateNew, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<AddValueOption[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input — 500ms to match MultiSelectWithCreate
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchTerm]);

  // Reset paging when search changes
  useEffect(() => {
    setOptions([]);
    setPage(1);
    setHasMore(false);
  }, [debouncedSearch]);

  // Latest-ref for loadOptions. The parent (e.g. UnmatchedView /
  // SKUMatchedView via `loadValueOptionsForRow(index)`) is a curried
  // factory that returns a fresh function reference on every render,
  // and the callsite in ExpandedRowContent invokes
  // `loadValueOptions!(importedAttr.name)` inline — so `loadOptions`
  // is a NEW reference on every parent render. If we put it in the
  // effect's dep array, every parent re-render while this popup is
  // open re-fires the fetch (visible as a burst of GETs every time the
  // user ticks a checkbox: the tick mutates parent state ->
  // re-render -> new loadOptions -> effect re-runs -> another GET).
  // The latest-ref pattern keeps the freshest implementation reachable
  // from the effect without putting it on the dep array.
  const loadOptionsRef = useRef(loadOptions);
  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  });

  // Fetch options when opened or page/search changes
  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    loadOptionsRef.current(debouncedSearch, page)
      .then(({ options: newOpts, hasMore: more }) => {
        setHasMore(more);
        setOptions((prev) =>
          page === 1 ? newOpts : [...prev, ...newOpts.filter((o) => !prev.some((p) => p.value === o.value))]
        );
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, debouncedSearch, page]);

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setPage((p) => p + 1),
      { threshold: 0.1 }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, isLoading]);

  // Position popover relative to trigger (viewport-fixed, escapes overflow clipping)
  useLayoutEffect(() => {
    if (!isOpen) {
      setPopupPos(null);
      return;
    }
    const updatePos = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const POPUP_W = 240;
      const POPUP_H = 280;
      const maxLeft = Math.max(0, window.innerWidth - POPUP_W - 4);
      const left = Math.max(4, Math.min(rect.left, maxLeft));
      const spaceBelow = window.innerHeight - rect.bottom - 4;
      const top = spaceBelow >= POPUP_H ? rect.bottom + 4 : Math.max(4, rect.top - POPUP_H - 4);
      setPopupPos({ top, left });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (popoverRef.current && popoverRef.current.contains(tgt)) return;
      if (triggerRef.current && triggerRef.current.contains(tgt)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Reset the search input whenever the popover closes — the user expects a
  // fresh search on every reopen, regardless of how it was dismissed (toggle
  // off via the `+` trigger, outside click, Create button, etc.).
  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  const selectedSet = new Set(selectedValues);

  return (
    <>
      <button data-testid="expanded-row-expandedrowcontent-button-1"
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen((v) => !v)}
        disabled={disabled}
        title="Add values"
        className={`tw-inline-flex tw-items-center tw-justify-center tw-h-[28px] tw-w-[28px] tw-rounded-[3px] tw-border-[0.5px] tw-border-solid tw-border-edge-strong tw-bg-surface tw-text-ink-secondary hover:tw-bg-surface-hover-strong ${disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer'}`}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeWidth="1.5" d="M8 3v10M3 8h10" />
        </svg>
      </button>
      {isOpen && popupPos && (
        <div
          ref={popoverRef}
          className="tw-fixed tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-lg tw-z-[9999] tw-w-60 tw-max-h-[280px] tw-flex tw-flex-col"
          style={{ top: popupPos.top, left: popupPos.left }}
        >
          <div className="tw-px-3 tw-py-2 tw-border-b tw-border-edge">
            <Button
              variant="tertiary"
              onClick={() => {
                setIsOpen(false);
                onCreateNew();
              }}
              className="hover:tw-bg-surface tw-p-0"
            >
              Create
            </Button>
          </div>
          <div className="tw-px-3 tw-py-2">
            <div className="tw-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-border-edge-strong tw-bg-surface tw-px-2 focus-within:tw-border-accent focus-within:tw-bg-surface">
              <input data-testid="expanded-row-expandedrowcontent-input-2"
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-outline-none tw-text-ink tw-py-2 tw-text-sm placeholder:tw-text-ink-secondary"
              />
              <svg className="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-text-ink-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="tw-flex-1 tw-overflow-y-auto tw-px-3 tw-pb-2">
            {options.length === 0 && !isLoading && (
              <div className="tw-text-sm tw-text-ink-secondary tw-italic tw-py-2">No values available</div>
            )}
            {options.map((opt) => {
              const checked = selectedSet.has(opt.value);
              return (
                <div key={opt.value} className="tw-py-1">
                  <Checkbox
                    label={opt.label}
                    checked={checked}
                    onChange={() => (checked ? onRemove(opt.value) : onAdd(opt.value))}
                  />
                </div>
              );
            })}
            {hasMore && !isLoading && <div ref={sentinelRef} className="tw-h-1" />}
            {isLoading && <div className="tw-text-sm tw-text-ink-secondary tw-py-2">Loading...</div>}
          </div>
        </div>
      )}
    </>
  );
};

/**
 * ExpandedRowContent Component
 *
 * Returns a single <tr> containing a <td colSpan={12}> with nested tables:
 * - Header table (pinned above scroll)
 * - Data rows table (scrollable when >5 rows)
 * - Footer table (pinned below scroll)
 *
 * All three nested tables share the same <colgroup> via colWidths prop
 * to keep columns aligned with the parent table.
 *
 * In edit mode:
 * - Shows "Action" column header with delete icons in each row
 * - Footer metadata fields become editable TextField inputs
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=12424-24095
 */
const ExpandedRowContent = ({
  catalogAttributes = [],
  importedAttributes = [],
  catalogMetadata,
  importedMetadata,
  isEditMode = false,
  onAttributeChange,
  onDeleteAttribute,
  onDeleteAttributeValue,
  onRevertAttribute,
  onRevertAttributeValue,
  onRestoreAttributeValue,
  onUndoRestoreAttributeValue,
  onModalAttrValuesSave,
  onEditModeEnter,
  onMetadataChange,
  onAddAttributeValue,
  onAddNewAttribute,
  onSelectNewAttributeName,
  loadValueOptions,
  loadAttributeOptions,
  onCreateNewValue,
  onCreateNewAttribute,
  colWidths: colWidthsProp,
  productName = '',
  singleSection = false,
  hasCheckboxCol = true,
}: ExpandedRowContentProps) => {
  const colWidths = colWidthsProp ?? (singleSection ? SINGLE_SECTION_COL_WIDTHS : DEFAULT_COL_WIDTHS);

  // Per-attribute options cache for the value-picker. Both the inline `+`
  // popover (InlineAddValueButton) and the "Add More Values" panel in
  // AttributeValuesModal call `loadValueOptions(attrName)(search, page)`
  // for the same attribute against the same channel-scoped endpoint —
  // without a cache, opening "Show All" right after clicking `+`
  // re-fires the identical GET. The cache memos results by
  // (attributeName, search, page) and is wired in via a wrapper around
  // `loadValueOptions` below.
  //
  // Stored as a ref-backed Map so cache writes don't trigger re-renders
  // and the cache survives the component's own re-renders. Selection
  // filtering (`gridOptions` in AttributeValuesModal) is independent
  // of this cache, so no invalidation is needed when the user adds or
  // removes a value — the cache holds raw options; what's already
  // selected is filtered out at render time on both surfaces.
  const valueOptionsCacheRef = useRef<Map<string, Map<string, { options: { value: string; label: string }[]; hasMore: boolean }>>>(new Map());
  const cachedLoadValueOptions = useMemo(() => {
    if (!loadValueOptions) return undefined;
    return (attributeName: string) => {
      const innerLoader = loadValueOptions(attributeName);
      return async (search: string, page: number) => {
        const cacheKey = `${search}::${page}`;
        let attrCache = valueOptionsCacheRef.current.get(attributeName);
        if (!attrCache) {
          attrCache = new Map();
          valueOptionsCacheRef.current.set(attributeName, attrCache);
        }
        const hit = attrCache.get(cacheKey);
        if (hit) return hit;
        const result = await innerLoader(search, page);
        attrCache.set(cacheKey, result);
        return result;
      };
    };
  }, [loadValueOptions]);

  // Invalidate the value-options cache for an attribute whenever its
  // `editedValues` length changes. The cache holds raw loader pages keyed by
  // (search, page) — fine for inline ×/+ flows (where the data stays the same
  // and only the selection filter changes), but stale after a "Create New
  // Value" creates a brand-new entry in the backend: searching for the new
  // value would return the pre-create cached page and silently drop it. The
  // length-change signal covers both directions (create adds, delete via ×
  // shouldn't matter but harmless to clear). Pending-name placeholders are
  // skipped because they don't map to a real loader key yet.
  const prevEditedValuesLenRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    importedAttributes.forEach((attr) => {
      if (!attr.name || attr.name.startsWith('__pending_')) return;
      const currLen = attr.editedValues?.length ?? -1;
      const prevLen = prevEditedValuesLenRef.current.get(attr.name);
      if (prevLen !== undefined && prevLen !== currLen) {
        valueOptionsCacheRef.current.delete(attr.name);
      }
      prevEditedValuesLenRef.current.set(attr.name, currLen);
    });
  }, [importedAttributes]);

  // For single-section layouts, figure out how many outer cols the Attribute
  // Value cell needs to span so that the first 2–3 inner cells line up with
  // [checkbox, #, Product] (or [#, Product] when hasCheckboxCol=false) in
  // the outer table. Falls back to spanning "the rest" when widths aren't
  // the expected outer layout (e.g. short SINGLE_SECTION_COL_WIDTHS default).
  const singleSectionLeadingCount = hasCheckboxCol ? 3 : 2; // spacer? + # + Attribute
  const singleSectionValueSpan = Math.max(1, colWidths.length - singleSectionLeadingCount);
  // Attribute Values modal state.
  // `originalImportedValues` / `originalImportedValueStatuses` snapshot the
  // pre-edit-session baseline (i.e. `attr.values` / `attr.valueStatuses`,
  // ignoring `editedValues`). The modal's Reset uses this to undo session
  // edits — `importedValues` itself is the display set with newly-added
  // values already baked in, so resetting to it wouldn't actually clear them.
  const [attrValuesModal, setAttrValuesModal] = useState<{
    isOpen: boolean;
    attributeName: string;
    catalogValues: string[];
    importedValues: string[];
    importedValueStatuses: ValueStatus[];
    originalImportedValues: string[];
    originalImportedValueStatuses: ValueStatus[];
    isCatalogSection: boolean;
  }>({
    isOpen: false,
    attributeName: '',
    catalogValues: [],
    importedValues: [],
    importedValueStatuses: [],
    originalImportedValues: [],
    originalImportedValueStatuses: [],
    isCatalogSection: false,
  });

  // Shared description expand/collapse — one toggle, controls both sides.
  // Collapsed by default per AC; the outer row toggle is independent.
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Hide the Show More toggle when the description fits on a single line —
  // nothing to reveal. We measure overflow on the collapsed spans (where the
  // tw-truncate class is what produces a scrollWidth > clientWidth signal).
  const catalogDescRef = useRef<HTMLSpanElement | null>(null);
  const importedDescRef = useRef<HTMLSpanElement | null>(null);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);
  useLayoutEffect(() => {
    if (isDescriptionExpanded) return; // refs are unmounted while expanded
    const cat = catalogDescRef.current;
    const imp = importedDescRef.current;
    const catOver = cat ? cat.scrollWidth > cat.clientWidth : false;
    const impOver = imp ? imp.scrollWidth > imp.clientWidth : false;
    setDescriptionOverflows(catOver || impOver);
  }, [isDescriptionExpanded, catalogMetadata?.description, importedMetadata?.description]);

  // Pure derivation of the AttributeValuesModal's value props from the
  // currently-known catalog / imported attribute. Shared between the Show All
  // click handler and the resync effect below — when a value is created via
  // the Create flow while the modal is open, the row's `editedValues` gains
  // the new value and this helper re-derives the chip set so the modal
  // displays it (as an `'added'` blue chip) without the user having to close
  // and reopen Show All.
  const deriveAttrValuesModalData = (
    catalogAttr: ProductAttribute | undefined,
    importedAttr: ProductAttribute | undefined,
  ) => {
    const catalogVals = catalogAttr ? getValues(catalogAttr) : [];
    const importedVals = importedAttr ? getValues(importedAttr) : [];
    const originalStatuses = importedAttr?.valueStatuses || [];

    if (importedAttr?.editedValues !== undefined) {
      const editedSet = new Set(importedAttr.editedValues);
      const statuses: ValueStatus[] = importedVals.map((val, i) => {
        const orig = originalStatuses[i] || 'unchanged';
        if (editedSet.has(val)) {
          return orig === 'removed' ? 'unchanged' : orig;
        }
        return 'removed';
      });
      const importedSet = new Set(importedVals);
      const catalogOnlyVals = catalogVals.filter((v) => !importedSet.has(v));
      const removedCatalogVals = catalogOnlyVals.filter((v) => !editedSet.has(v));
      const importedAndCatalogSet = new Set<string>([...importedVals, ...catalogVals]);
      const newlyAddedVals = (importedAttr.editedValues || []).filter(
        (v) => !importedAndCatalogSet.has(v),
      );
      const finalImportedVals = [
        ...importedVals,
        ...removedCatalogVals,
        ...newlyAddedVals,
      ];
      const finalStatuses: ValueStatus[] = [
        ...statuses,
        ...removedCatalogVals.map(() => 'removed' as ValueStatus),
        ...newlyAddedVals.map(() => 'added' as ValueStatus),
      ];
      return {
        catalogValues: catalogVals,
        importedValues: finalImportedVals,
        importedValueStatuses: finalStatuses,
        originalImportedValues: importedVals,
        originalImportedValueStatuses: originalStatuses,
      };
    }

    const statuses: ValueStatus[] = importedVals.map(
      (_, i) => originalStatuses[i] || 'unchanged',
    );
    return {
      catalogValues: catalogVals,
      importedValues: importedVals,
      importedValueStatuses: statuses,
      originalImportedValues: importedVals,
      originalImportedValueStatuses: statuses,
    };
  };

  // Resync the open Show All modal with the latest row state. Fires whenever
  // the parent's attribute arrays change while the modal is open — most
  // importantly when the inline Create flow adds a value through
  // `handleAddAttributeValue`, which mutates `importedAttr.editedValues` but
  // not the snapshot inside `attrValuesModal`.
  useEffect(() => {
    if (!attrValuesModal.isOpen) return;
    const name = attrValuesModal.attributeName;
    const catalogAttr = catalogAttributes.find((a) => a.name === name);
    const importedAttr = importedAttributes.find((a) => a.name === name);
    if (!catalogAttr && !importedAttr) return;
    const next = deriveAttrValuesModalData(catalogAttr, importedAttr);
    setAttrValuesModal((prev) => ({ ...prev, ...next }));
    // deriveAttrValuesModalData is a stable closure over `getValues` (utility
    // import), so it doesn't need to be in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogAttributes, importedAttributes, attrValuesModal.isOpen, attrValuesModal.attributeName]);

  const openAttrValuesModal = (
    attributeName: string,
    catalogAttr: ProductAttribute | undefined,
    importedAttr: ProductAttribute | undefined,
    isCatalogSection: boolean = false
  ) => {
    const catalogVals = catalogAttr ? getValues(catalogAttr) : [];
    const importedVals = importedAttr ? getValues(importedAttr) : [];
    const originalStatuses = importedAttr?.valueStatuses || [];

    if (importedAttr?.editedValues !== undefined) {
      // editedValues is set — derive statuses from it
      const editedSet = new Set(importedAttr.editedValues);
      const statuses: ValueStatus[] = importedVals.map((val, i) => {
        const orig = originalStatuses[i] || 'unchanged';
        if (editedSet.has(val)) {
          // Value is kept — if it was originally 'removed', show as 'unchanged' (restored)
          return orig === 'removed' ? 'unchanged' : orig;
        }
        // Value is not in editedValues — mark as removed
        return 'removed';
      });

      // Catalog-only values not in editedValues should appear as removed imports
      const importedSet = new Set(importedVals);
      const catalogOnlyVals = catalogVals.filter((v) => !importedSet.has(v));
      const removedCatalogVals = catalogOnlyVals.filter((v) => !editedSet.has(v));

      // Edit-only values: present in `editedValues` but not in the original
      // imports and not in the catalog. These are values the user just added
      // via the inline `+` button. Without this branch they vanish from the
      // modal snapshot (the modal builds badges from `importedValues`, which
      // came from `getValues(importedAttr)` — i.e. `attr.values`, the
      // original imports, not the live edited set), so a user who added a
      // value via `+` then clicked "Show All" saw the chip missing in the
      // modal even though the row chips below already reflected it. Mark
      // them as `'added'` so they render with the blue added-style badge,
      // matching how the inline `+` flow displays just-added values.
      const importedAndCatalogSet = new Set<string>([...importedVals, ...catalogVals]);
      const newlyAddedVals = (importedAttr.editedValues || []).filter(
        (v) => !importedAndCatalogSet.has(v)
      );

      const finalImportedVals = [
        ...importedVals,
        ...removedCatalogVals,
        ...newlyAddedVals,
      ];
      const finalStatuses: ValueStatus[] = [
        ...statuses,
        ...removedCatalogVals.map(() => 'removed' as ValueStatus),
        ...newlyAddedVals.map(() => 'added' as ValueStatus),
      ];

      setAttrValuesModal({
        isOpen: true,
        attributeName,
        catalogValues: catalogVals,
        importedValues: finalImportedVals,
        importedValueStatuses: finalStatuses,
        // Pre-session baseline: the raw `attr.values` and `attr.valueStatuses`
        // before this session's `editedValues` was layered on. Reset
        // restores chips to this — newly-added values (in `finalImportedVals`
        // but not in `importedVals`) are dropped, and previously-removed
        // values are restored to their pre-edit `'removed'` status.
        originalImportedValues: importedVals,
        originalImportedValueStatuses: originalStatuses,
        isCatalogSection,
      });
    } else {
      // No edits — use original statuses
      const statuses: ValueStatus[] = importedVals.map((_, i) => originalStatuses[i] || 'unchanged');

      setAttrValuesModal({
        isOpen: true,
        attributeName,
        catalogValues: catalogVals,
        importedValues: importedVals,
        importedValueStatuses: statuses,
        // No session edits — display and reset baselines are identical.
        originalImportedValues: importedVals,
        originalImportedValueStatuses: statuses,
        isCatalogSection,
      });
    }
  };

  // Combine attributes to create matched rows
  // Match by attribute name, handling new attributes that only exist in imported
  const getMatchedRows = () => {
    const allAttributeNames = new Set([
      ...catalogAttributes.map((a) => a.name),
      ...importedAttributes.map((a) => a.name),
    ]);

    return Array.from(allAttributeNames).map((name) => {
      const catalogAttr = catalogAttributes.find((a) => a.name === name);
      const importedAttr = importedAttributes.find((a) => a.name === name);
      return { name, catalogAttr, importedAttr };
    }).sort((a, b) => {
      const aNew = a.importedAttr?.isNew ? 1 : 0;
      const bNew = b.importedAttr?.isNew ? 1 : 0;
      if (aNew !== bNew) return bNew - aNew;

      const aUpdated = a.importedAttr?.isUpdated ? 1 : 0;
      const bUpdated = b.importedAttr?.isUpdated ? 1 : 0;
      if (aUpdated !== bUpdated) return bUpdated - aUpdated;

      // AB#13810 AC 3: custom attributes sit at the BOTTOM while they are idle. A
      // custom attribute mid-update (isNew / isUpdated / isRemoved) has already been
      // placed above by the keys before this one — that is AB#13811 AC 1, "moves up
      // into the regular section" — so only the idle ones sink here.
      const aIdleCustom = isIdleCustomRow(a) ? 1 : 0;
      const bIdleCustom = isIdleCustomRow(b) ? 1 : 0;
      if (aIdleCustom !== bIdleCustom) return aIdleCustom - bIdleCustom;

      return 0;
    });
  };

  const isCustomRow = (row: { catalogAttr?: ProductAttribute; importedAttr?: ProductAttribute }) =>
    !!(row.importedAttr?.isCustom || row.catalogAttr?.isCustom);

  const isIdleCustomRow = (row: { catalogAttr?: ProductAttribute; importedAttr?: ProductAttribute }) =>
    isCustomRow(row)
    && !row.importedAttr?.isNew
    && !row.importedAttr?.isUpdated
    && !row.importedAttr?.isRemoved;

  /**
   * The tag glyph that precedes a custom attribute's name (AB#13810 AC 4/5). Rendered on
   * BOTH sides and in every mode — before, during and after a conflict (AB#13811 AC 3) —
   * so the same helper is used by every name cell below.
   */
  const renderCustomTag = (side: 'catalog' | 'imported', name: string) => (
    <CustomAttributeTag data-testid={`${side}-attr-custom-tag-${name}`} />
  );

  const matchedRows = getMatchedRows();
  // Inner scroll was previously enabled for >5 attributes. With
  // `scrollbar-gutter: stable` applied to the header/data/metadata wrappers,
  // the inner tables ended up ~15px narrower than the outer row's table,
  // which shifted the catalog↔imported divider noticeably leftward relative
  // to the outer divider. Letting the expanded section grow naturally keeps
  // all three column tracks identical to the outer row, so the partition
  // lines up perfectly no matter how many attributes the product has.
  const shouldScroll = false;

  const gutterStyle: React.CSSProperties = {};

  // Render shared colgroup from colWidths prop
  const renderColgroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );

  // Determine if attribute has multiple values that need badge rendering
  const hasMultipleValues = (attr: ProductAttribute): boolean => {
    return getValues(attr).length > 1;
  };

  // Render a single value badge with proper styling
  // Figma: rounded-[3px], border 0.5px, min-h 28px, px-2, py-0.5
  // Determine badge type for a single value based on its status
  type BadgeType = 'catalog' | 'catalog-outline' | 'new' | 'updated' | 'removed' | 'normal';

  const getBadgeTypeForStatus = (status?: ValueStatus): BadgeType => {
    if (!status) return 'normal';
    switch (status) {
      case 'added': return 'new';
      case 'removed': return 'removed';
      case 'unchanged': return 'normal';
      default: return 'normal';
    }
  };

  /**
   * BadgeType -> DS `Tag` props (§ 67).
   *
   * The three coloured types report a STATE, so they take a tone; `catalog`,
   * `catalog-outline` and `normal` merely DESCRIBE a value, so they take the quiet
   * `meta` pair. That split is the design system's own grammar, and it is what the
   * six hand-rolled `bg-*-100 / border-*-500` maps were approximating.
   *
   * ## The glyphs came off info and warning (2026-08-31)
   *
   * They were there because a diff row puts "added" (info) and "updated" (warning)
   * side by side, identical in shape, so the hue was the only carrier of the
   * difference (WCAG SC 1.4.1). Asked for on a screenshot of this grid: at chip
   * density — six rows, two columns, up to four chips a row — a glyph on every one
   * of them read as clutter rather than information.
   *
   * Two things about the change worth knowing before reversing it:
   *
   * - **The glyph never helped assistive tech.** It is `aria-hidden` by design, so a
   *   screen reader heard "description" with no hint it was new, and this grid's
   *   headings are "Attribute" / "Attribute Value" — the state is disclosed nowhere
   *   else. `stateLabel` now carries it, which is a net gain over the glyph.
   * - **For sighted users the hue is now the sole visual carrier** on info and
   *   warning. That is the accepted trade, not an oversight. The way back without
   *   the glyph is a visible state column in this grid, which is a bigger change.
   *
   * `danger` KEEPS its glyph. It marks a value staged for deletion — the one state
   * here that is destructive and pending — and it pairs with a return-arrow affordance
   * rather than a `×`, so that row already reads differently at a glance. Reserving
   * the glyph for it makes it mean something.
   */
  const tagPropsForBadge = (
    type: BadgeType,
  ): Pick<TagProps, 'tone' | 'variant' | 'showToneGlyph' | 'stateLabel'> => {
    switch (type) {
      case 'new': return { tone: 'info', stateLabel: 'new' };
      case 'updated': return { tone: 'warning', stateLabel: 'updated' };
      case 'removed': return { tone: 'danger', showToneGlyph: true, stateLabel: 'removed' };
      /* catalog | catalog-outline | normal — a value that describes, not one chosen. */
      default: return { variant: 'meta' };
    }
  };

  const renderSingleBadge = (
    value: string,
    type: BadgeType,
    key?: string | number,
    testId?: string,
  ) => (
    <Tag key={key} label={value} data-testid={testId} {...tagPropsForBadge(type)} />
  );

  /** A value chip the user can take back out. Same handler, same value, DS chrome. */
  const renderBadgeWithRemove = (
    value: string,
    type: BadgeType,
    onRemove: () => void,
    key?: string | number,
    testId?: string,
  ) => (
    <Tag
      key={key}
      label={value}
      data-testid={testId}
      onRemove={onRemove}
      {...tagPropsForBadge(type)}
    />
  );

  /**
   * A value staged for deletion. The affordance UNDOES rather than removes, so the
   * glyph is a return arrow and the accessible name says "Restore" — a `×` here would
   * read as "delete it again", which is what the hand-rolled version's title said
   * while showing the return arrow.
   */
  const renderPendingDeleteBadge = (
    value: string,
    onRevert: () => void,
    key?: string | number,
    testId?: string,
  ) => (
    <Tag
      key={key}
      label={value}
      data-testid={testId}
      tone="danger"
      showToneGlyph
      stateLabel="staged for deletion"
      onRemove={onRevert}
      removeIcon={<Return size={12} aria-hidden="true" />}
      removeLabel={`Restore ${value}`}
    />
  );

  // Render multiple value badges with "Show All" truncation
  // Figma node 12452:29834: shows badges inline, then "+ N" (grey-700, SemiBold 14px)
  // and "Show All" link (blue-500, Medium 12px) — now opens Attribute Values modal
  const renderMultiValueBadges = (
    attr: ProductAttribute,
    type: BadgeType,
    rowKey: string,
    isCatalogSide: boolean = false,
    catalogAttr?: ProductAttribute,
    importedAttr?: ProductAttribute
  ) => {
    const values = getValues(attr);
    if (values.length === 0) {
      return (
        <Typography variant="title" className="tw-text-ink-secondary">
          -
        </Typography>
      );
    }

    // Determine per-value badge type
    const getPerValueBadgeType = (index: number): BadgeType => {
      if (isCatalogSide) return type;
      // Use per-value status if available
      if (attr.valueStatuses && attr.valueStatuses[index]) {
        const statusType = getBadgeTypeForStatus(attr.valueStatuses[index]);
        // Unchanged values inherit the parent badge type
        if (statusType === 'normal' && (type === 'catalog' || type === 'catalog-outline')) return 'catalog-outline';
        return statusType;
      }
      // Fallback to attribute-level type
      return type;
    };

    // Single value — render as before
    if (values.length === 1) {
      return renderSingleBadge(values[0], getPerValueBadgeType(0));
    }

    // Include catalog-only values in the count to match the modal's merged total
    const catalogValues = catalogAttr ? getValues(catalogAttr) : [];
    const importedSet = new Set(values);
    const catalogOnlyCount = catalogValues.filter(v => !importedSet.has(v)).length;
    const totalCount = values.length + catalogOnlyCount;
    const visibleValues = values.slice(0, MAX_VISIBLE_VALUES);
    const remainingCount = totalCount - MAX_VISIBLE_VALUES;

    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        {visibleValues.map((val, i) => renderSingleBadge(val, getPerValueBadgeType(i), i))}
        {remainingCount > 0 && (
          <>
            {/* "+ N" counter — Figma: grey-700 (#505866), SemiBold 14px */}
            <Typography variant="title-header" className="tw-text-ink-secondary tw-whitespace-nowrap">
              + {remainingCount}
            </Typography>
            {/* "Show All" opens Attribute Values modal — Figma node 12456:32089 */}
            <button data-testid="expanded-row-expandedrowcontent-button-5"
              onClick={() => openAttrValuesModal(attr.name, catalogAttr, importedAttr, isCatalogSide)}
              className="tw-text-sub-body-supporting tw-text-accent tw-whitespace-nowrap tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0"
            >
              Show All
            </button>
          </>
        )}
      </div>
    );
  };

  // Render value cell content — uses multi-badge when multiple values, single badge otherwise
  const renderValueContent = (
    attr: ProductAttribute | undefined,
    type: BadgeType,
    rowKey: string,
    isCatalogSide: boolean = false,
    catalogAttr?: ProductAttribute,
    importedAttr?: ProductAttribute
  ) => {
    if (!attr || !attr.value || attr.value === '-') {
      return (
        <Typography variant="title" className="tw-text-ink-secondary">
          -
        </Typography>
      );
    }

    // Unchanged values: outlined boxes (white background) on both sides
    if (type === 'normal') {
      if (hasMultipleValues(attr)) {
        return renderMultiValueBadges(attr, 'catalog-outline', rowKey, isCatalogSide, catalogAttr, importedAttr);
      }
      return renderSingleBadge(attr.value, 'catalog-outline');
    }

    if (hasMultipleValues(attr)) {
      return renderMultiValueBadges(attr, type, rowKey, isCatalogSide, catalogAttr, importedAttr);
    }

    // For single value, use per-value status if available
    if (!isCatalogSide && attr.valueStatuses && attr.valueStatuses[0]) {
      const statusType = getBadgeTypeForStatus(attr.valueStatuses[0]);
      // Unchanged values use outlined (white) style
      const resolvedType = (statusType === 'normal' && (type === 'catalog' || type === 'catalog-outline')) ? 'catalog-outline' : statusType;
      return renderSingleBadge(attr.value, resolvedType);
    }

    return renderSingleBadge(attr.value, type);
  };

  // Footer metadata field (read-only)
  // Figma: Labels are always grey-600; imported values are grey-900, catalog values are grey-600
  const renderMetadataField = (
    label: string,
    value: string,
    isImported: boolean,
    isUpdated?: boolean
  ) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">
        {label}
      </span>
      {isImported && isUpdated ? (
        <span
          title={value || '-'}
          className="tw-inline-flex tw-items-center tw-max-w-full tw-px-2 tw-py-0.5 tw-rounded-[3px] tw-min-h-[28px] tw-border-[0.5px] tw-border-solid tw-text-xs tw-font-semibold tw-bg-tone-warning-tint tw-border-tone-warning-glyph tw-text-tone-warning-ink"
        >
          <span className="tw-truncate">{value || '-'}</span>
        </span>
      ) : (
        <span
          title={value || '-'}
          className={`tw-block tw-truncate tw-text-xs tw-font-semibold ${
            isImported ? 'tw-text-ink' : 'tw-text-ink-secondary'
          }`}
        >
          {value || '-'}
        </span>
      )}
    </div>
  );

  // Editable metadata field for edit mode
  const renderEditableMetadataField = (
    label: string,
    value: string,
    field: keyof ProductMetadata
  ) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span className="tw-text-xs tw-font-normal tw-text-ink">{label}</span>
      <input data-testid="expanded-row-expandedrowcontent-input-6"
        type="text"
        value={value || ''}
        onChange={(e) => onMetadataChange?.(field, e.target.value)}
        className="tw-text-xs tw-font-semibold tw-text-ink tw-px-2 tw-py-1 tw-rounded tw-bg-surface tw-w-[90px] tw-border tw-border-tone-warning-glyph"
      />
    </div>
  );

  return (
    <tr>
      <td colSpan={12} className="tw-p-0 tw-border-t tw-border-edge">
        {/* HEADER — pinned outside scroll */}
        <div style={gutterStyle}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            {renderColgroup()}
            <thead>
              <tr className="tw-h-[44px] tw-bg-surface-sunken">
                {singleSection ? (
                  <>
                    {/* Empty spacer (only when the outer table has a checkbox
                        col — skips it for views like Missing Mapping where
                        the # column is col 1). */}
                    {hasCheckboxCol && <td className="tw-bg-surface-sunken"></td>}
                    {/* # Column header */}
                    <td className="tw-px-2 tw-py-2.5 tw-text-center tw-bg-surface-sunken">
                      <span className="tw-inline-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                        <Hashtag />
                      </span>
                    </td>
                    {/* Attribute header — spans 1 outer col (Product) */}
                    <td className="tw-px-3 tw-py-2.5 tw-text-left tw-bg-surface-sunken">
                      <Typography variant="title-header" className="tw-text-ink-secondary">
                        Attribute
                      </Typography>
                    </td>
                    {/* Attribute Value header — spans the remaining outer cols */}
                    <td colSpan={singleSectionValueSpan} className="tw-px-3 tw-py-2.5 tw-text-left tw-bg-surface-sunken">
                      <Typography variant="title-header" className="tw-text-ink-secondary">
                        Attribute Value
                      </Typography>
                    </td>
                  </>
                ) : (
                  <>
                    {/* Empty for checkbox column */}
                    <td className="tw-bg-surface-sunken"></td>
                    {/* # Column header — same flex-center wrapper as the
                        outer table header so the symbol lands at the exact
                        centerline of the 3% col. */}
                    <td className="tw-px-2 tw-py-2.5 tw-text-center tw-bg-surface-sunken">
                      <span className="tw-inline-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                        <Hashtag />
                      </span>
                    </td>
                    {/* Attribute header */}
                    <td className="tw-px-3 tw-py-2.5 tw-text-left tw-bg-surface-sunken">
                      <Typography variant="title-header" className="tw-text-ink-secondary">
                        Attribute
                      </Typography>
                    </td>
                    {/* Attribute Value header - spans Category + Attributes columns */}
                    <td
                      colSpan={2}
                      className="tw-px-3 tw-py-2.5 tw-text-left tw-bg-surface-sunken"
                    >
                      <Typography variant="title-header" className="tw-text-ink-secondary">
                        Attribute Value
                      </Typography>
                    </td>
                    {/* Imported Attribute header - with border-left for divider alignment */}
                    <td
                      colSpan={2}
                      className="tw-px-3 tw-py-2.5 tw-text-left tw-border-l tw-border-edge tw-bg-surface-sunken"
                    >
                      <Typography variant="title-header" className="tw-text-ink-secondary">
                        Attribute
                      </Typography>
                    </td>
                    {/* Imported Attribute Value header */}
                    <td
                      colSpan={5}
                      className="tw-px-3 tw-py-2.5 tw-text-left tw-bg-surface-sunken"
                    >
                      <Typography variant="title-header" className="tw-text-ink-secondary">
                        Attribute Value
                      </Typography>
                    </td>
                  </>
                )}
              </tr>
            </thead>
          </table>
        </div>

        {/* DATA ROWS — scrollable when >5 */}
        <div
          style={{
            maxHeight: shouldScroll ? `${MAX_VISIBLE_ROWS * ROW_HEIGHT_PX}px` : undefined,
            overflowY: shouldScroll ? 'auto' : undefined,
            ...(shouldScroll ? ({ scrollbarGutter: 'stable' } as React.CSSProperties) : {}),
          }}
        >
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            {renderColgroup()}
            <tbody>
              {matchedRows.map((row, index) => {
                const catalogAttr = row.catalogAttr;
                const importedAttr = row.importedAttr;
                const isNewAttribute = importedAttr?.isNew && !catalogAttr;
                // Attribute removed status — respects user edits:
                // editedValues defined + has values → user restored something → NOT removed
                // editedValues defined + empty → user removed all → removed
                // editedValues undefined → no edits → use original API status
                const allValuesRemoved = importedAttr?.valueStatuses
                  && importedAttr.valueStatuses.length > 0
                  && importedAttr.valueStatuses.every((s) => s === 'removed');
                const originallyRemoved = !!(importedAttr?.isRemoved || allValuesRemoved);
                const isRemovedAttribute = importedAttr?.editedValues !== undefined
                  ? importedAttr.editedValues.length === 0
                  : originallyRemoved;
                const isUpdatedValue = importedAttr?.isUpdated && !isRemovedAttribute;
                const hasCatalogValue = catalogAttr && catalogAttr.value;
                const rowIsCustom = isCustomRow(row);

                // Determine fallback badge type for imported values (used when no per-value statuses)
                const importedBadgeType: BadgeType = isNewAttribute
                  ? 'new'
                  : isRemovedAttribute
                  ? 'removed'
                  : isUpdatedValue
                  ? 'updated'
                  : 'normal';

                // Catalog side always renders as plain text — grey highlighting goes on the imported side
                const catalogBadgeType: BadgeType = 'normal';

                if (singleSection) {
                  return (
                    <tr
                      key={row.name || index}
                      className="tw-h-auto tw-min-h-[40px] tw-bg-surface tw-border-t tw-border-edge"
                    >
                      {/* Empty spacer (only when outer has checkbox col) */}
                      {hasCheckboxCol && <td></td>}
                      {/* # Column */}
                      <td className="tw-px-2 tw-py-2 tw-text-center tw-align-top">
                        <Typography variant="title-header" className="tw-text-ink">
                          {index + 1}
                        </Typography>
                      </td>
                      {/* Attribute name — spans 1 outer col (Product) */}
                      <td className="tw-px-3 tw-py-2 tw-align-top">
                        <div className="tw-flex tw-items-center tw-gap-1 tw-min-w-0">
                          {rowIsCustom && importedAttr && renderCustomTag('imported', row.name)}
                          <Typography
                            variant="title-header"
                            className="tw-text-ink tw-truncate tw-min-w-0"
                            title={importedAttr ? importedAttr.name : undefined}
                          >
                            {importedAttr ? importedAttr.name : '-'}
                          </Typography>
                        </div>
                      </td>
                      {/* Attribute Value — spans the remaining outer cols */}
                      <td colSpan={singleSectionValueSpan} className="tw-px-3 tw-py-2 tw-align-top">
                        {importedAttr ? (
                          renderValueContent(importedAttr, importedBadgeType, `imported-${row.name}`, false, catalogAttr, importedAttr)
                        ) : (
                          <Typography variant="title" className="tw-text-ink-secondary">
                            -
                          </Typography>
                        )}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={row.name || index}
                    className="tw-h-auto tw-min-h-[40px] tw-bg-surface tw-border-t tw-border-edge"
                  >
                    {/* Empty for checkbox column */}
                    <td></td>
                    {/* # Column */}
                    <td className="tw-px-2 tw-py-2 tw-text-center tw-align-top">
                      <Typography variant="title-header" className="tw-text-ink">
                        {index + 1}
                      </Typography>
                    </td>
                    {/* Catalog Attribute */}
                    <td className="tw-px-3 tw-py-2 tw-align-top">
                      {/* AB#14850: this cell is a single 19% track, so an unbreakable name
                          (e.g. `engineering_specification`) spilled under the value chip.
                          Truncate with an ellipsis like the value chips; full name on hover. */}
                      <div className="tw-flex tw-items-center tw-gap-1 tw-min-w-0">
                        {rowIsCustom && catalogAttr && renderCustomTag('catalog', row.name)}
                        <Typography
                          variant="title"
                          className="tw-text-ink-secondary tw-truncate tw-min-w-0"
                          title={catalogAttr ? catalogAttr.name : undefined}
                          data-testid={`catalog-attr-name-${row.name}`}
                        >
                          {catalogAttr ? catalogAttr.name : '-'}
                        </Typography>
                      </div>
                    </td>
                    {/* Catalog Attribute Value - spans Category + Attributes */}
                    <td colSpan={2} className="tw-px-3 tw-py-2 tw-align-top">
                      {hasCatalogValue ? (
                        renderValueContent(catalogAttr, catalogBadgeType, `catalog-${row.name}`, true, catalogAttr, importedAttr)
                      ) : (
                        <Typography variant="title" className="tw-text-ink-secondary">
                          -
                        </Typography>
                      )}
                    </td>
                    {/* Imported Attribute - with border-left for divider */}
                    <td colSpan={2} className="tw-px-3 tw-py-2 tw-border-l tw-border-edge tw-align-top">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                        {rowIsCustom && importedAttr && !importedAttr.isPendingNew && renderCustomTag('imported', row.name)}
                        {isEditMode && importedAttr?.isPendingNew ? (
                          loadAttributeOptions ? (
                            <SingleSelectWithCreate
                              value={importedAttr.name?.startsWith('__pending_') ? '' : importedAttr.name || ''}
                              onChange={(val) => {
                                const importedAttrIdx = importedAttributes.findIndex((a) => a === importedAttr);
                                if (importedAttrIdx >= 0) onSelectNewAttributeName?.(importedAttrIdx, val);
                              }}
                              options={[]}
                              loadOptions={loadAttributeOptions()}
                              onCreateNew={() => {
                                const importedAttrIdx = importedAttributes.findIndex((a) => a === importedAttr);
                                if (importedAttrIdx >= 0) onCreateNewAttribute?.(importedAttrIdx);
                              }}
                              placeholder="Select attribute"
                            />
                          ) : (
                            <Typography variant="title" className="tw-text-ink-secondary tw-italic">
                              Select attribute
                            </Typography>
                          )
                        ) : isEditMode && importedAttr ? (
                          (importedAttr.editedValues !== undefined && importedAttr.editedValues.length === 0) || (isRemovedAttribute && !(importedAttr.editedValues && importedAttr.editedValues.length > 0)) ? (
                            renderPendingDeleteBadge(
                              importedAttr.name,
                              () => {
                                const importedAttrIdx = importedAttributes.findIndex((a) => a.name === importedAttr.name);
                                if (importedAttrIdx >= 0) {
                                  if (isRemovedAttribute && importedAttr.editedValues === undefined) {
                                    // Removed by connector — restore all values
                                    const allValues = getValues(importedAttr);
                                    allValues.forEach((_, vi) => onRestoreAttributeValue?.(importedAttrIdx, vi));
                                  } else {
                                    // Removed by user edit — revert editedValues
                                    onRevertAttribute?.(importedAttrIdx);
                                  }
                                }
                              },
                              undefined,
                              `catalog-attr-${importedAttr.name}`,
                            )
                          ) : (
                            <div className="tw-flex tw-items-center tw-gap-1">
                              {(isNewAttribute || isUpdatedValue) ? (
                                renderBadgeWithRemove(
                                  importedAttr.name,
                                  isNewAttribute ? 'new' : 'updated',
                                  () => {
                                    const importedAttrIdx = importedAttributes.findIndex((a) => a.name === importedAttr.name);
                                    if (importedAttrIdx >= 0) onDeleteAttribute?.(importedAttrIdx);
                                  },
                                  undefined,
                                  `catalog-attr-${importedAttr.name}`,
                                )
                              ) : (
                                renderBadgeWithRemove(
                                  importedAttr.name,
                                  'catalog',
                                  () => {
                                    const importedAttrIdx = importedAttributes.findIndex((a) => a.name === importedAttr.name);
                                    if (importedAttrIdx >= 0) onDeleteAttribute?.(importedAttrIdx);
                                  },
                                  undefined,
                                  `catalog-attr-${importedAttr.name}`,
                                )
                              )}
                            </div>
                          )
                        ) : (
                          <>
                            {/* "New" tag removed — blue color badge already indicates new state */}
                            {importedAttr &&
                              (isNewAttribute ? (
                                renderSingleBadge(importedAttr.name, 'new')
                              ) : isUpdatedValue ? (
                                renderSingleBadge(importedAttr.name, 'updated')
                              ) : isRemovedAttribute ? (
                                renderSingleBadge(importedAttr.name, 'removed')
                              ) : (
                                <Typography
                                  variant="title-header"
                                  className="tw-text-ink tw-truncate tw-min-w-0"
                                  title={importedAttr.name}
                                >
                                  {importedAttr.name}
                                </Typography>
                              ))}
                          </>
                        )}
                      </div>
                    </td>
                    {/* Imported Attribute Value */}
                    <td colSpan={5} className="tw-px-3 tw-py-2 tw-align-top">
                      {isEditMode && importedAttr ? (
                        (() => {
                          const originalValues = getValues(importedAttr);
                          // Newly-added values (in editedValues but not in the original imported list).
                          // Append them so they render as chips like everything else.
                          const addedValues = (importedAttr.editedValues ?? []).filter(
                            (v) => !originalValues.includes(v)
                          );
                          const allValues = [...originalValues, ...addedValues];
                          const importedAttrIdx = importedAttributes.findIndex((a) => a.name === importedAttr.name);
                          // Include catalog-only values in the count to match the modal's merged total
                          const catalogVals = catalogAttr ? getValues(catalogAttr) : [];
                          const importedSet = new Set(allValues);
                          const catalogOnlyCount = catalogVals.filter((v: string) => !importedSet.has(v)).length;
                          const totalCount = allValues.length + catalogOnlyCount;
                          const visibleValues = allValues.slice(0, MAX_VISIBLE_VALUES);
                          const remainingCount = totalCount - MAX_VISIBLE_VALUES;

                          const editedSet = importedAttr.editedValues !== undefined ? new Set(importedAttr.editedValues) : null;
                          const isEdited = editedSet !== null;
                          // Pending-new attrs with empty editedValues mean "no values picked yet",
                          // not "user deleted everything" — keep the inline + active for them.
                          const isAttrDeleted = isEdited && importedAttr.editedValues!.length === 0 && !importedAttr.isPendingNew;

                          const renderEditBadge = (val: string, vi: number) => {
                            const origStatus = importedAttr.valueStatuses?.[vi] || 'unchanged';
                            const isKept = !isEdited || editedSet!.has(val);
                            // Newly-added values (just appended via the inline `+` or
                            // the modal picker) live at indices past the original
                            // imports — `allValues = [...originalValues, ...addedValues]`
                            // — and `valueStatuses` only covers the originals, so
                            // their `origStatus` resolves to `'unchanged'` and the
                            // status-driven branch below would paint them white
                            // (`catalog-outline`). The user expects them to read as
                            // additions, same blue style as a server-tagged `added`
                            // value, so we look them up by membership in
                            // `addedValues` and force the badge type to `'new'`.
                            const isNewlyAdded = vi >= originalValues.length;

                            if (isAttrDeleted) {
                              // Entire attribute has been removed (every value
                              // deleted, `editedValues` empty). Revert is
                              // still per-value here so the user can restore
                              // them one at a time — calling
                              // `onRevertAttribute` would wipe `editedValues`
                              // and bring every chip back at once, which
                              // surprised a reviewer who only wanted to undo
                              // a single accidental delete (visible symptom:
                              // "reverting any value brings all three back").
                              // If a "Revert entire attribute" affordance is
                              // needed it should live as a separate button
                              // outside the chip's × icon.
                              return renderPendingDeleteBadge(
                                val,
                                () => { if (importedAttrIdx >= 0) onRevertAttributeValue?.(importedAttrIdx, vi); },
                                vi,
                                `catalog-value-${importedAttr.name}-${val}`,
                              );
                            }
                            if (isEdited && !isKept) {
                              // Value was removed by user edit
                              return renderPendingDeleteBadge(
                                val,
                                () => { if (importedAttrIdx >= 0) onRevertAttributeValue?.(importedAttrIdx, vi); },
                                vi,
                                `catalog-value-${importedAttr.name}-${val}`,
                              );
                            }
                            if (!isEdited && origStatus === 'removed') {
                              // Originally removed, not yet edited — allow restore
                              return renderPendingDeleteBadge(
                                val,
                                () => { if (importedAttrIdx >= 0) onRestoreAttributeValue?.(importedAttrIdx, vi); },
                                vi,
                                `catalog-value-${importedAttr.name}-${val}`,
                              );
                            }
                            if (isEdited && isKept && origStatus === 'removed') {
                              // Was removed, user restored it — show as grey with undo
                              return renderBadgeWithRemove(
                                val,
                                'catalog',
                                () => { if (importedAttrIdx >= 0) onUndoRestoreAttributeValue?.(importedAttrIdx, vi); },
                                vi,
                                `catalog-value-${importedAttr.name}-${val}`,
                              );
                            }

                            // Determine per-value badge type from status
                            // 'added' → blue (new), inline-`+`/modal-added → blue,
                            // 'unchanged' in new attr → blue, otherwise → white
                            const perValueType: BadgeType =
                              origStatus === 'added' ? 'new'
                              : isNewlyAdded ? 'new'
                              : isNewAttribute ? 'new'
                              : 'catalog-outline';

                            return renderBadgeWithRemove(
                              val,
                              perValueType,
                              () => onDeleteAttributeValue?.(importedAttrIdx, vi),
                              vi,
                              `catalog-value-${importedAttr.name}-${val}`,
                            );
                          };

                          // Inline + button: shown only when there's no overflow
                          // (no "Show All" link). For overflow rows, the popup
                          // is the editor and the + lives inside it.
                          const canShowInlineAdd = remainingCount <= 0
                            && !isAttrDeleted
                            && loadValueOptions
                            && (!importedAttr.isPendingNew || (!!importedAttr.name?.trim() && !importedAttr.name.startsWith('__pending_')));
                          const inlineAddDisabled = importedAttr.isPendingNew && (!importedAttr.name?.trim() || importedAttr.name.startsWith('__pending_'));

                          return (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                              {allValues.length === 0 && !canShowInlineAdd && (
                                <Typography variant="title" className="tw-text-ink-secondary">-</Typography>
                              )}
                              {visibleValues.map((val, vi) => renderEditBadge(val, vi))}
                              {remainingCount > 0 && (
                                <>
                                  <Typography variant="title-header" className="tw-text-ink-secondary tw-whitespace-nowrap">
                                    + {remainingCount}
                                  </Typography>
                                  <button data-testid={`expanded-row-expandedrowcontent-button-7-${index}`}
                                    onClick={() => openAttrValuesModal(importedAttr.name, catalogAttr, importedAttr, false)}
                                    className="tw-text-sub-body-supporting tw-text-accent tw-whitespace-nowrap tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0"
                                  >
                                    Show All
                                  </button>
                                </>
                              )}
                              {canShowInlineAdd && (
                                <div className="tw-ml-auto">
                                  <InlineAddValueButton
                                    selectedValues={importedAttr.editedValues ?? allValues}
                                    loadOptions={cachedLoadValueOptions!(importedAttr.name)}
                                    onAdd={(val) => importedAttrIdx >= 0 && onAddAttributeValue?.(importedAttrIdx, val)}
                                    onRemove={(val) => {
                                      if (importedAttrIdx < 0) return;
                                      const idx = allValues.indexOf(val);
                                      if (idx >= 0) onDeleteAttributeValue?.(importedAttrIdx, idx);
                                    }}
                                    onCreateNew={() => onCreateNewValue?.(importedAttr.name)}
                                    disabled={inlineAddDisabled}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : importedAttr ? (
                        renderValueContent(importedAttr, importedBadgeType, `imported-${row.name}`, false, catalogAttr, importedAttr)
                      ) : (
                        <Typography variant="title" className="tw-text-ink-secondary">
                          -
                        </Typography>
                      )}
                    </td>
                  </tr>
                );
              })}
              {/* "+ Add Attribute" row — only in edit mode on the dual-section
                  layout. Single-section tabs (Missing Mapping etc.) don't
                  surface attribute editing. */}
              {isEditMode && !singleSection && onAddNewAttribute && (
                <tr className="tw-bg-surface tw-border-t tw-border-edge">
                  <td></td>
                  <td></td>
                  <td colSpan={3}></td>
                  <td colSpan={2} className="tw-px-3 tw-py-2 tw-border-l tw-border-edge">
                    <button data-testid="expanded-row-expandedrowcontent-button-8"
                      type="button"
                      onClick={() => onAddNewAttribute?.()}
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-ink-inverse tw-bg-accent-bold hover:tw-bg-accent-bold-hover tw-border-none tw-rounded tw-cursor-pointer tw-px-4 tw-py-2"
                    >
                      Add Attribute
                    </button>
                  </td>
                  <td colSpan={5}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER — pinned outside scroll, 3 rows per Figma node 12424-24371 */}
        {!singleSection && (catalogMetadata || importedMetadata) && (
          <div style={gutterStyle}>
            <table className="tw-w-full tw-border-collapse tw-table-fixed">
              {renderColgroup()}
              <tbody>
                {/* Row 1: SKU / Price / UPC / Model Number */}
                <tr className="tw-h-[40px] tw-bg-surface tw-border-t tw-border-edge">
                  <td></td>
                  <td colSpan={4} className="tw-px-3 tw-py-2">
                    {catalogMetadata && (
                      <div className="tw-grid tw-grid-cols-4 tw-gap-6 tw-items-start">
                        {renderMetadataField('SKU', catalogMetadata.sku, false)}
                        {renderMetadataField('Price', catalogMetadata.price, false)}
                        {renderMetadataField('UPC', catalogMetadata.upc, false)}
                        {renderMetadataField('Model Number', catalogMetadata.modelNumber, false)}
                      </div>
                    )}
                  </td>
                  <td colSpan={7} className="tw-px-3 tw-py-2 tw-border-l tw-border-edge">
                    {importedMetadata && (
                      <div className="tw-grid tw-grid-cols-4 tw-gap-6 tw-items-start">
                        {isEditMode ? (
                          <>
                            {renderMetadataField('SKU', importedMetadata.sku, true)}
                            {renderEditableMetadataField('Price', importedMetadata.price, 'price')}
                            {renderEditableMetadataField('UPC', importedMetadata.upc, 'upc')}
                            {renderEditableMetadataField('Model Number', importedMetadata.modelNumber, 'modelNumber')}
                          </>
                        ) : (
                          <>
                            {renderMetadataField('SKU', importedMetadata.sku, true)}
                            {renderMetadataField('Price', importedMetadata.price, true, importedMetadata.updatedFields?.price)}
                            {renderMetadataField('UPC', importedMetadata.upc, true, importedMetadata.updatedFields?.upc)}
                            {renderMetadataField('Model Number', importedMetadata.modelNumber, true, importedMetadata.updatedFields?.modelNumber)}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {/* Row 2: PLS / Parent ID / Version / Real SKU */}
                <tr className="tw-h-[40px] tw-bg-surface tw-border-t tw-border-edge">
                  <td></td>
                  <td colSpan={4} className="tw-px-3 tw-py-2">
                    {catalogMetadata && (
                      <div className="tw-grid tw-grid-cols-4 tw-gap-6 tw-items-start">
                        {renderMetadataField('PLS', catalogMetadata.pls || '', false)}
                        {renderMetadataField('Parent ID', catalogMetadata.parentId || '', false)}
                        {renderMetadataField('Version', catalogMetadata.version || '', false)}
                        {renderMetadataField('Real SKU', catalogMetadata.realSku || '', false)}
                      </div>
                    )}
                  </td>
                  <td colSpan={7} className="tw-px-3 tw-py-2 tw-border-l tw-border-edge">
                    {importedMetadata && (
                      <div className="tw-grid tw-grid-cols-4 tw-gap-6 tw-items-start">
                        {isEditMode ? (
                          <>
                            <div className="tw-flex tw-flex-col tw-gap-0.5">
                              <span className="tw-text-xs tw-font-normal tw-text-ink">PLS</span>
                              <select data-testid="expanded-row-expandedrowcontent-select-9"
                                value={importedMetadata.pls || ''}
                                onChange={(e) => onMetadataChange?.('pls', e.target.value)}
                                className={`tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded tw-bg-surface tw-w-[110px] tw-border tw-cursor-pointer tw-appearance-none tw-bg-no-repeat tw-bg-[length:12px] tw-bg-[right_8px_center] tw-pr-6 ${
                                  importedMetadata.pls === 'Deleted'
                                    ? 'tw-text-tone-danger-ink tw-border-tone-danger-glyph'
                                    : !importedMetadata.pls
                                      ? 'tw-text-ink tw-border-tone-danger-glyph'
                                      : 'tw-text-ink tw-border-tone-warning-glyph'
                                }`}
                                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23505866' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")` }}
                              >
                                <option value="Enabled">Enabled</option>
                                <option value="Disabled">Disabled</option>
                                <option value="Deleted" className="tw-text-tone-danger-ink">Deleted</option>
                              </select>
                            </div>
                            {renderEditableMetadataField('Parent ID', importedMetadata.parentId || '', 'parentId')}
                            {renderEditableMetadataField('Version', importedMetadata.version || '', 'version')}
                            {renderMetadataField('Real SKU', importedMetadata.realSku || '', true)}
                          </>
                        ) : (
                          <>
                            {renderMetadataField('PLS', importedMetadata.pls || '', true, importedMetadata.updatedFields?.pls)}
                            {renderMetadataField('Parent ID', importedMetadata.parentId || '', true, importedMetadata.updatedFields?.parentId)}
                            {renderMetadataField('Version', importedMetadata.version || '', true, importedMetadata.updatedFields?.version)}
                            {renderMetadataField('Real SKU', importedMetadata.realSku || '', true, importedMetadata.updatedFields?.realSku)}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {/* Row 3: Description — single shared expand/collapse toggle on the imported (right) side.
                    Collapsed by default; expanded shows full text on both sides simultaneously. */}
                <tr className="tw-bg-surface tw-border-t tw-border-edge">
                  <td></td>
                  <td colSpan={4} className="tw-px-3 tw-py-2 tw-align-top">
                    {catalogMetadata && (
                      <div className="tw-flex tw-flex-col tw-gap-0.5">
                        <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">Description</span>
                        {isDescriptionExpanded ? (
                          catalogMetadata.description ? (
                            <span className="tw-text-xs tw-font-semibold tw-text-ink-secondary tw-whitespace-pre-wrap tw-break-words">
                              {catalogMetadata.description}
                            </span>
                          ) : (
                            <span className="tw-text-xs tw-italic tw-text-ink-secondary">
                              — No description provided —
                            </span>
                          )
                        ) : (
                          <span
                            ref={catalogDescRef}
                            className="tw-text-xs tw-font-semibold tw-text-ink-secondary tw-truncate tw-block"
                          >
                            {catalogMetadata.description || '-'}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td colSpan={7} className="tw-px-3 tw-py-2 tw-border-l tw-border-edge tw-align-top">
                    {importedMetadata && (
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-flex-1 tw-min-w-0">
                          <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">Description</span>
                          {isDescriptionExpanded ? (
                            isEditMode ? (
                              <textarea data-testid="expanded-row-expandedrowcontent-textarea-10"
                                value={importedMetadata.description || ''}
                                onChange={(e) => onMetadataChange?.('description', e.target.value)}
                                rows={3}
                                className="tw-text-xs tw-font-semibold tw-text-ink tw-px-2 tw-py-1 tw-rounded tw-bg-surface tw-w-full tw-border tw-border-tone-warning-glyph tw-resize-none"
                              />
                            ) : importedMetadata.description ? (
                              <span className={`tw-text-xs tw-font-semibold tw-text-ink tw-whitespace-pre-wrap tw-break-words ${
                                importedMetadata.updatedFields?.description
                                  ? 'tw-inline-block tw-bg-tone-warning-tint tw-border-[0.5px] tw-border-solid tw-border-tone-warning-glyph tw-rounded-[3px] tw-px-2 tw-py-0.5'
                                  : ''
                              }`}>
                                {importedMetadata.description}
                              </span>
                            ) : (
                              <span className="tw-text-xs tw-italic tw-text-ink-secondary">
                                — No description provided —
                              </span>
                            )
                          ) : (
                            <span
                              ref={importedDescRef}
                              className={`tw-text-xs tw-font-semibold tw-text-ink tw-truncate tw-block ${
                                importedMetadata.updatedFields?.description
                                  ? 'tw-bg-tone-warning-tint tw-border-[0.5px] tw-border-solid tw-border-tone-warning-glyph tw-rounded-[3px] tw-px-2 tw-py-0.5'
                                  : ''
                              }`}
                            >
                              {importedMetadata.description || '-'}
                            </span>
                          )}
                        </div>
                        {/* Toggle visible when: edit mode (need access to the textarea),
                            description currently expanded (so user can collapse back),
                            or there's truncated content worth revealing. */}
                        {(isEditMode || isDescriptionExpanded || descriptionOverflows) && (
                          <button data-testid="expanded-row-expandedrowcontent-button-11"
                            type="button"
                            onClick={() => setIsDescriptionExpanded((v) => !v)}
                            aria-expanded={isDescriptionExpanded}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-accent tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-whitespace-nowrap"
                          >
                            {isDescriptionExpanded ? 'Show Less' : 'Show More'}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={`tw-transition-transform ${isDescriptionExpanded ? 'tw-rotate-180' : ''}`}
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </td>

      {/* Attribute Values Modal — opened via "Show All" button */}
      <AttributeValuesModal
        isOpen={attrValuesModal.isOpen}
        onClose={() => setAttrValuesModal((prev) => ({ ...prev, isOpen: false }))}
        productName={productName}
        attributeName={attrValuesModal.attributeName}
        catalogValues={attrValuesModal.catalogValues}
        importedValues={attrValuesModal.importedValues}
        importedValueStatuses={attrValuesModal.importedValueStatuses}
        originalImportedValues={attrValuesModal.originalImportedValues}
        originalImportedValueStatuses={attrValuesModal.originalImportedValueStatuses}
        isEditMode={isEditMode}
        onEditModeEnter={onEditModeEnter}
        onSave={
          (onModalAttrValuesSave && !attrValuesModal.isCatalogSection)
            ? (changes: { valuesToKeep: string[] }) => {
                onModalAttrValuesSave(
                  attrValuesModal.attributeName,
                  changes.valuesToKeep
                );
                setAttrValuesModal((prev) => ({ ...prev, isOpen: false }));
              }
            : undefined
        }
        // 11662: "Add More Values" picker — only the imported (editable) side
        // gets the loader; the catalog-side modal opens read-only. Uses the
        // cached wrapper so the modal hits the same options the inline `+`
        // popover has already loaded for this attribute (same channel-scoped
        // endpoint, same (search, page) keys) instead of re-firing.
        loadValueOptions={
          cachedLoadValueOptions && !attrValuesModal.isCatalogSection
            ? cachedLoadValueOptions(attrValuesModal.attributeName)
            : undefined
        }
        onAddValue={(val) => {
          const idx = importedAttributes.findIndex((a) => a.name === attrValuesModal.attributeName);
          if (idx >= 0) onAddAttributeValue?.(idx, val);
        }}
        onCreateNew={() => onCreateNewValue?.(attrValuesModal.attributeName)}
      />
    </tr>
  );
};

export default React.memo(ExpandedRowContent);
